import React from 'react';
import styled from 'styled-components';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import { H2, ButtonPrimary } from '@yuhu-inc/yuhui';

import PropertyMultiSelect from './form_fields/PropertyMultiSelect';
import DocumentType from './form_fields/DocumentType';
import LeaseItemType from './form_fields/LeaseItemType';
import TemplateName from './form_fields/TemplateName';
import TemplateDescription from './form_fields/TemplateDescription';
import BaseDocumentTemplateSelector from './form_fields/BaseDocumentTemplateSelector';
import { FIXED_ADDENDUM, CONDITIONAL_ADDENDUM } from '../CreateDocumentTemplateDialog';

import { getTranslatedString } from 'helpers/i18n';
import { media } from 'helpers/MediaHelper';

import { StyledFooterText } from './form_fields/shared_styles/footers';

const StyledForm = styled(Form)`
  margin-top: 20%;
  width: 50%;
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${media.mobile`
  width: 90%;
  `};
`;

const StyledInner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 5rem;
  height: 700px;
  ${media.mobile`
  max-height: 550px;
  `};
`;

const StyledButton = styled(ButtonPrimary)`
  margin-top: 25px;
  width: fit-content;
  align-self: center;
`;

const StyledH2 = styled(H2)`
  margin-bottom: 30px;
`;

const DocumentTemplateSetupPage = ({ setTemplate, forward, documentTemplate = {}, ...rest }) => {
  const TemplateSetupSchema = Yup.object().shape({
    property_uuids: Yup.array().required(
      getTranslatedString('landlordPortal.documentTemplate.selectAtLeastOneProperty')
    ),
    signable_doc_type: Yup.string().required(
      getTranslatedString('landlordPortal.documentTemplate.selectDocumentType')
    ),
    conditional_addendum_name: Yup.string().when('signable_doc_type', {
      is: CONDITIONAL_ADDENDUM, // if signable doc type is set as conditional addendum
      then: Yup.string().required(getTranslatedString('required')),
      otherwise: Yup.string().notRequired(),
    }),
    base_document_template_ids: Yup.array().when('signable_doc_type', {
      is: FIXED_ADDENDUM,
      then: Yup.array().required(getTranslatedString('required')),
      otherwise: Yup.array().notRequired(),
    }),
    title: Yup.string().required(getTranslatedString('required')),
    description: Yup.string().required(getTranslatedString('required')),
  });

  const initialValues = {
    property_uuids: documentTemplate.property_uuids || [],
    signable_doc_type: documentTemplate.signable_doc_type || undefined,
    base_document_template_ids: documentTemplate.base_document_template_ids || [],
    conditional_addendum_name: documentTemplate.conditional_addendum_name || undefined,
    has_warning: documentTemplate.has_warning || false,
    title: documentTemplate.title || undefined,
    description: documentTemplate.description || undefined,
  };

  const handleSubmit = (values) => {
    setTemplate((template) => {
      return { ...template, ...values };
    });

    if (!values.has_warning) {
      forward();
    }
  };
  return (
    <Formik
      validationSchema={TemplateSetupSchema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ ...rest }) => (
        <StyledForm {...rest}>
          <StyledH2 contentKey="landlordPortal.documentTemplate.letsSetUpTemplate" centered />
          <StyledInner>
            <div style={{ marginBottom: '-30px' }}>
              <PropertyMultiSelect {...rest} />
            </div>
            <DocumentType {...rest} />
            <LeaseItemType {...rest} />
            <BaseDocumentTemplateSelector {...rest} />
            <TemplateName {...rest} />
            <TemplateDescription {...rest} />
            <StyledButton contentKey="landlordPortal.inspections.next" />
            <StyledFooterText>
              {getTranslatedString('landlordPortal.documentTemplate.completedPagesAutoSave')}
            </StyledFooterText>
          </StyledInner>
        </StyledForm>
      )}
    </Formik>
  );
};

export default DocumentTemplateSetupPage;
